import React, { useEffect, useState } from 'react';
import { RefreshCw, Check, AlertTriangle, Download, X, Copy, ExternalLink } from 'lucide-react';
import { syncSource, syncLast, syncCheck, syncPlan, syncApply, syncFile, syncVerify } from '../lib/api';
import { useCategories } from '../context/CategoriesContext';

const errText = (err) => err?.response?.data?.detail || 'Something went wrong — please try again. Nothing was changed.';
const isPreview = () => window.location.hostname.includes('preview.emergentagent.com');
const LIVE_ADMIN = 'https://owen-dilley.com/admin';
const PREVIEW_MSG = "You're in the preview. This button copies into the site you're on, so open owen-dilley.com/admin and tap it there. Nothing was changed.";
const isSelfCopy = (e) => e?.response?.status === 400 && /this site|same site/.test(e?.response?.data?.detail || '');

export default function SyncPanel() {
  const { reload } = useCategories();
  const [url, setUrl] = useState('');
  const [plan, setPlan] = useState(null);
  const [phase, setPhase] = useState('idle'); // idle | planning | ready | copying | done
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [failed, setFailed] = useState([]);
  const [copied, setCopied] = useState(0);
  const [result, setResult] = useState(null);
  const [check, setCheck] = useState(null);
  const [err, setErr] = useState(null);
  const [saved, setSaved] = useState('');
  const [last, setLast] = useState(null);
  const [diff, setDiff] = useState(null);
  const [checking, setChecking] = useState(false);
  const runCheck = async () => {
    setErr(null); setChecking(true);
    try { setDiff(await syncCheck(url)); }
    catch (e) { setErr(isPreview() && isSelfCopy(e) ? PREVIEW_MSG : errText(e)); }
    setChecking(false);
  };
  useEffect(() => {
    syncSource().then(setSaved).catch(() => {});
    syncLast().then(setLast).catch(() => {});
  }, []);
  const source = url.trim().replace(/\/+$/, '') || saved;

  const start = async () => {
    setErr(null); setResult(null); setCheck(null); setFailed([]); setCopied(0); setPhase('planning');
    try { setPlan(await syncPlan(url)); setPhase('ready'); }
    catch (e) { setErr(isPreview() && isSelfCopy(e) ? PREVIEW_MSG : errText(e)); setPhase('idle'); }
  };

  const copyFiles = async (names) => {
    const bad = [];
    let ok = 0;
    for (let i = 0; i < names.length; i += 1) {
      setProgress({ done: i, total: names.length });
      // One at a time so large videos never compete for the connection.
      try { await syncFile(plan.job_id, names[i]); ok += 1; } catch { bad.push(names[i]); }
    }
    setProgress({ done: names.length, total: names.length });
    setCopied((c) => c + ok);
    setFailed(bad);
    const v = await syncVerify(plan.job_id).catch(() => ({ ok: false, checks: [] }));
    setCheck(v);
    if (v.last) setLast(v.last);
    setDiff(null);
  };

  const confirm = async () => {
    setErr(null); setPhase('copying');
    try {
      const res = await syncApply(plan.job_id);
      setResult(res);
      await copyFiles(res.files);
      reload();
      setPhase('done');
    } catch (e) { setErr(errText(e)); setPhase('ready'); }
  };

  const retry = async () => { setPhase('copying'); await copyFiles(failed); setPhase('done'); };
  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div className="border-2 border-[#3a2512] bg-[#0f0704] p-4 sm:p-6 rounded-lg space-y-4 h-fit lg:col-span-2" data-testid="admin-sync-panel">
      <div className="font-osd text-[#f5a623] text-sm tracking-widest">// COPY FROM PREVIEW <span className="text-[#8b6a3e]">· PREVIEW → LIVE SITE</span></div>
      <p className="font-body text-xs sm:text-sm text-[#a88552]">
        Makes this site an exact copy of the preview: broadcasts, covers, channels, About page, footer links, Resume PDF, and 3D faces/looks.
        Matching items (same channel + title) are updated. Anything here that isn't in the preview is removed.
      </p>
      {isPreview() && (
        <div className="flex flex-wrap items-center gap-2 font-osd text-xs text-[#ffc968] border border-[#f5a623]/40 bg-[#1a0e05] px-3 py-2 rounded" data-testid="sync-preview-note">
          <span className="flex-1 min-w-[200px]">YOU'RE IN THE PREVIEW. RUN THE COPY FROM OWEN-DILLEY.COM/ADMIN.</span>
          <LiveAdminBtn testId="sync-note-live-admin-btn" />
        </div>
      )}
      <LastCopied last={last} />
      <ChangeCheck diff={diff} checking={checking} onCheck={runCheck} />
      {source && <SourceLine url={source} typed={!!url.trim()} />}
      <label className="block">
        <span className="font-osd text-xs text-[#a88552] tracking-widest">PREVIEW ADDRESS <span className="opacity-60">(optional · leave blank to use the saved one)</span></span>
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://….preview.emergentagent.com"
               className="mt-1 w-full bg-[#1a0e05] border border-[#3a2512] text-[#ffe9b8] font-body text-sm px-3 py-2 rounded" data-testid="sync-preview-url" />
      </label>

      {phase !== 'ready' && phase !== 'copying' && (
        <BigBtn onClick={start} disabled={phase === 'planning'} testId="sync-start-btn">
          <Download className="w-4 h-4" /> {phase === 'planning' ? 'CHECKING PREVIEW…' : 'COPY FROM PREVIEW'}
        </BigBtn>
      )}

      {plan && phase === 'ready' && <Summary plan={plan} onConfirm={confirm} onCancel={() => { setPlan(null); setPhase('idle'); }} />}

      {phase === 'copying' && (
        <div className="space-y-2" data-testid="sync-progress">
          <div className="font-osd text-xs text-[#ffe9b8] tracking-widest truncate">
            {progress.total ? `COPYING FILE ${Math.min(progress.done + 1, progress.total)} / ${progress.total}` : 'COPYING RECORDS…'}
          </div>
          <div className="h-2 bg-[#2a1608] rounded overflow-hidden"><div className="h-full bg-[#f5a623] transition-[width]" style={{ width: `${pct}%` }} /></div>
        </div>
      )}

      {phase === 'done' && result && <Result result={result} copied={copied} failed={failed} check={check} onRetry={retry} />}

      {err && (
        <div className="flex flex-wrap items-center gap-2 font-osd text-xs text-[#ff6a3a] border border-[#ff6a3a]/50 bg-[#1a0e05] px-3 py-2 rounded" role="alert" data-testid="sync-error">
          <span className="flex-1 min-w-[200px]">{err}</span>
          {err === PREVIEW_MSG && <LiveAdminBtn testId="sync-error-live-admin-btn" />}
        </div>
      )}
    </div>
  );
}

function ChangeCheck({ diff, checking, onCheck }) {
  const parts = diff ? [
    diff.add && `${diff.add} NEW`, diff.changed && `${diff.changed} EDITED`, diff.remove && `${diff.remove} TO REMOVE`,
    ...diff.settings
  ].filter(Boolean) : [];
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="sync-check-row">
      <button type="button" onClick={onCheck} disabled={checking} data-testid="sync-check-btn"
              className="admin-action min-h-[40px] px-3 flex items-center gap-1.5 border-2 border-[#5a3418] text-[#ffc968] rounded font-osd text-xs tracking-widest hover:border-[#f5a623] transition-colors disabled:opacity-60">
        <RefreshCw className={`w-3.5 h-3.5 ${checking ? 'animate-spin' : ''}`} /> {checking ? 'CHECKING…' : 'CHECK FOR CHANGES'}
      </button>
      {diff && (diff.up_to_date ? (
        <span className="font-osd text-[11px] tracking-widest px-2 py-1 rounded-sm bg-[#9dff7a] text-[#1a0e05]" data-testid="sync-badge-uptodate">LIVE IS UP TO DATE</span>
      ) : (
        <span className="font-osd text-[11px] tracking-widest px-2 py-1 rounded-sm bg-[#ff8a3a] text-[#1a0e05] shadow-[0_0_10px_rgba(255,138,58,0.55)]" data-testid="sync-badge-outdated">
          PREVIEW HAS {diff.total} CHANGE{diff.total === 1 ? '' : 'S'} NOT ON LIVE · {parts.join(' · ')}
        </span>
      ))}
    </div>
  );
}

function LastCopied({ last }) {
  if (!last) {
    return <div className="font-osd text-xs tracking-widest text-[#8b6a3e]" data-testid="sync-last-copied">LAST COPIED: NEVER ON THIS SITE</div>;
  }
  const when = new Date(last.at).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
  const items = (last.created || 0) + (last.updated || 0);
  return (
    <div className="font-osd text-xs tracking-widest text-[#ffe9b8] flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="sync-last-copied">
      <span className="text-[#8b6a3e]">LAST COPIED:</span>
      <span data-testid="sync-last-time">{when}</span>
      <span className={last.ok ? 'text-[#9dff7a]' : 'text-[#ff8a5c]'} data-testid="sync-last-status">· {last.ok ? 'PASSED' : 'NOT FULLY DONE'}</span>
      <span data-testid="sync-last-counts">· {items} ITEMS ({last.created || 0} ADDED, {last.updated || 0} UPDATED, {last.removed || 0} REMOVED) · {last.files_copied || 0} FILES{last.failed ? ` · ${last.failed} FAILED` : ''}</span>
    </div>
  );
}

function LiveAdminBtn({ testId }) {
  return (
    <a href={LIVE_ADMIN} target="_blank" rel="noopener noreferrer" data-testid={testId}
       className="admin-action min-h-[40px] px-3 flex items-center gap-1.5 border-2 border-[#f5a623] text-[#ffc968] rounded font-osd text-xs tracking-widest hover:bg-[#f5a623] hover:text-[#1a0e05] transition-colors">
      <ExternalLink className="w-3.5 h-3.5" /> OPEN LIVE ADMIN
    </a>
  );
}

function SourceLine({ url, typed }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); } catch {
      const t = document.createElement('textarea'); t.value = url; document.body.appendChild(t); t.select(); document.execCommand('copy'); t.remove();
    }
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };
  const btn = 'admin-action min-h-[40px] px-3 flex items-center gap-1.5 border-2 border-[#5a3418] text-[#ffc968] rounded font-osd text-xs tracking-widest hover:border-[#f5a623] transition-colors';
  return (
    <div className="flex flex-wrap items-center gap-2 border border-[#3a2512] bg-[#1a0e05]/60 rounded px-3 py-2" data-testid="sync-source-line">
      <div className="font-osd text-xs tracking-widest text-[#f5a623] break-all flex-1 min-w-[200px]">
        COPIES FROM{typed ? ' (TYPED)' : ''}: <span className="text-[#ffe9b8]" data-testid="sync-source-url">{url}</span>
      </div>
      <button type="button" onClick={copy} className={btn} data-testid="sync-source-copy-btn">
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? 'COPIED' : 'COPY'}
      </button>
      <a href={url} target="_blank" rel="noopener noreferrer" className={btn} data-testid="sync-source-open-btn">
        <ExternalLink className="w-3.5 h-3.5" /> OPEN
      </a>
    </div>
  );
}

function Summary({ plan, onConfirm, onCancel }) {
  const s = plan.summary;
  return (
    <div className="space-y-3" data-testid="sync-summary">
      <div className="font-osd text-[11px] text-[#8b6a3e] tracking-widest break-all" data-testid="sync-source">SOURCE: {plan.source}</div>
      <div className="font-osd text-xs text-[#ffe9b8] tracking-widest border border-[#3a2512] rounded px-3 py-2.5 bg-[#1a0e05]/60">
        PREVIEW HAS: {s.videos} VIDEOS · {s.images} IMAGES · {s.models} MODELS · {s.channels} CHANNELS
        {s.about ? ' · ABOUT PAGE' : ''}{s.links ? ' · FOOTER LINKS' : ''}{s.pdf ? ' · RESUME PDF' : ''} · {s.files} FILES
      </div>
      <div className="font-osd text-xs tracking-widest text-[#ffe9b8]" data-testid="sync-diff">
        <span className="text-[#9dff7a]">+{s.add} ADD</span> · <span className="text-[#ffc968]">{s.update} UPDATE</span> ·{' '}
        <span className="text-[#ff8a5c]">−{s.remove} REMOVE</span>
      </div>
      {(s.remove > 0 || s.remove_channels > 0) && (
        <div className="flex items-start gap-2 font-osd text-xs text-[#ff8a5c] border border-[#7a2a14] bg-[#1a0704] rounded px-3 py-2" data-testid="sync-remove-warning">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{s.remove} LIVE ITEM{s.remove === 1 ? '' : 'S'}{s.remove_channels ? ` AND ${s.remove_channels} CHANNEL${s.remove_channels === 1 ? '' : 'S'}` : ''} NOT IN THE PREVIEW WILL BE DELETED. THIS CAN'T BE UNDONE.</span>
        </div>
      )}
      <div className="flex gap-2">
        <BigBtn onClick={onConfirm} testId="sync-confirm-btn"><Check className="w-4 h-4" /> CONFIRM</BigBtn>
        <button type="button" onClick={onCancel} data-testid="sync-cancel-btn"
                className="admin-action h-12 px-4 border-2 border-[#5a3418] text-[#d8b887] rounded font-osd text-xs tracking-widest hover:border-[#f5a623]">CANCEL</button>
      </div>
    </div>
  );
}

function Result({ result, copied, failed, check, onRetry }) {
  const passed = failed.length === 0 && check?.ok;
  return (
    <div className="space-y-3" data-testid="sync-result">
      <div className={`font-osd text-sm tracking-widest ${passed ? 'text-[#9dff7a]' : 'text-[#ff8a5c]'}`} data-testid={passed ? 'sync-complete' : 'sync-incomplete'}>
        {passed ? '✓ LIVE SITE NOW MATCHES PREVIEW' : '✕ COPY NOT FULLY DONE'}
      </div>
      <div className="font-osd text-xs text-[#ffe9b8] tracking-widest" data-testid="sync-counts">
        {result.created} ADDED · {result.updated} UPDATED · {result.removed} REMOVED{result.removed_channels ? ` · ${result.removed_channels} CHANNELS REMOVED` : ''} · {copied} FILES COPIED · {failed.length} FAILED
      </div>
      {failed.length > 0 && (
        <button type="button" onClick={onRetry} data-testid="sync-retry-btn" className="admin-action h-10 px-3 border-2 border-[#ff6a3a] text-[#ff8a5c] rounded flex items-center gap-1.5 font-osd text-xs">
          <RefreshCw className="w-4 h-4" /> RETRY {failed.length} FILE{failed.length === 1 ? '' : 'S'}
        </button>
      )}
      {check && (
        <ul className="space-y-1" data-testid="sync-checks">
          {check.checks.map((c) => (
            <li key={c.label} className={`flex items-center gap-2 font-osd text-xs ${c.ok ? 'text-[#9dff7a]' : 'text-[#ff8a5c]'}`} data-testid={`sync-check-${c.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
              {c.ok ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />} {c.label}{c.detail ? ` · ${c.detail}` : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BigBtn({ children, onClick, disabled, testId }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testId}
            className="admin-action flex-1 w-full h-12 flex items-center justify-center gap-2 bg-[#f5a623] text-[#1a0e05] font-osd tracking-widest px-5 rounded border-2 border-[#1a0e05] shadow-[3px_3px_0_#1a0e05] hover:bg-[#ffc968] transition-colors disabled:opacity-60">
      {children}
    </button>
  );
}
